import { Filter, RuleOperator, DateFilterOptions, filterOptionKeys } from 'src/app/modules/core/store/models/filters.model';
import booleanIntersects from '@turf/boolean-intersects';
import booleanDisjoint from '@turf/boolean-disjoint';
import moment from 'moment';

export interface IFieldFilterHandler {
  fieldMatchesFilter(value: any, filter: Filter, geofence?: { [documentUId: string]: string }): boolean;
}

export class FieldFilterHandler {
  public comparisonHandlers = {
    [RuleOperator.Equal]: this.equals.bind(this),
    [RuleOperator.NotEqual]: this.notEquals.bind(this),
    [RuleOperator.GreaterThan]: this.greaterThan.bind(this),
    [RuleOperator.GreaterThanEqualTo]: this.greaterThanOrEqualTo.bind(this),
    [RuleOperator.LessThan]: this.lessThan.bind(this),
    [RuleOperator.LessThanEqualTo]: this.lessThanOrEqualTo.bind(this),
    [RuleOperator.Contains]: this.contains.bind(this),
    [RuleOperator.Proximity]: this.proximity.bind(this),
    [RuleOperator.DateIsWithinLast]: this.dateIsWithinLast.bind(this),
    [RuleOperator.DoesNotContain]: this.doesNotContain.bind(this),
    [RuleOperator.DateIsNotWithinLast]: this.dateIsNotWithinLast.bind(this),
    [RuleOperator.IsEmpty]: this.isEmpty.bind(this),
    [RuleOperator.IsNotEmpty]: this.isNotEmpty.bind(this),
    [RuleOperator.In]: this.in.bind(this),
    [RuleOperator.DateIsWithinNext]: this.dateIsWithinNext.bind(this),
    [RuleOperator.DateIsNotWithinNext]: this.dateIsNotWithinNext.bind(this),
    [RuleOperator.DateIsGreaterThan]: this.dateIsGreaterThan.bind(this),
    [RuleOperator.DateIsLessThan]: this.dateIsLessThan.bind(this),
    [RuleOperator.ContainsNoCase]: this.containsNoCase.bind(this),
    [RuleOperator.DoesNotContainNoCase]: this.doesNotContainNoCase.bind(this),
    [RuleOperator.NotIn]: this.notIn.bind(this),
    [RuleOperator.InASingleGeofence]: this.inASingleGeofence.bind(this),
    [RuleOperator.NotInASingleGeofence]: this.notInASingleGeofence.bind(this),
    [RuleOperator.EqualNoCase]: this.equalsNoCase.bind(this),
    [RuleOperator.NotEqualNoCase]: this.notEqualsNoCase.bind(this)
  };

  public equals(value: any, filter: Filter): boolean {
    return value === filter.value;
  }

  public notEquals(value: any, filter: Filter): boolean {
    return value !== filter.value;
  }
  
  public equalsNoCase(value: any, filter: Filter): boolean {
    if (typeof value === 'string' && typeof filter.value === 'string') {
      return value.toLocaleLowerCase() === filter.value.toLocaleLowerCase();
    }

    return this.equals(value, filter);
  }

  public notEqualsNoCase(value: any, filter: Filter): boolean {
    if (typeof value === 'string' && typeof filter.value === 'string') {
      return value.toLocaleLowerCase() !== filter.value.toLocaleLowerCase();
    }

    return this.notEquals(value, filter);
  }

  public greaterThan(value: any, filter: Filter): boolean {
    return value > filter.value;
  }

  public greaterThanOrEqualTo(value: any, filter: Filter): boolean {
    return value >= filter.value;
  }

  public lessThan(value: any, filter: Filter): boolean {
    return value < filter.value;
  }

  public lessThanOrEqualTo(value: any, filter: Filter): boolean {
    return value <= filter.value;
  }

  public contains(value: any, filter: Filter): boolean {
    return !!value && value.toString().indexOf(filter.value) > -1;
  }

  public proximity(value: any, filter: Filter): boolean {
    return true;
  }

  public dateIsWithinLast(value: any, filter: Filter): boolean {
    const comparisonDate = this.processDateWithinLastCriteria(filter);
    const valueDate = moment.utc(value);
    // Set now slightly in the future to avoid issues with local time being slightly off
    const now = moment.utc().add(5, 'minutes');
    const comparisonValue = valueDate.isAfter(comparisonDate) && valueDate.isBefore(now);
    return comparisonValue;
  }

  public doesNotContain(value: any, filter: Filter): boolean {
    return !!value && value.toString().indexOf(filter.value) === -1;
  }

  public dateIsNotWithinLast(value: any, filter: Filter): boolean {
    const comparisonValue = this.dateIsWithinLast(value, filter);
    return !comparisonValue;
  }

  public isEmpty(value: any, filter: Filter): boolean {
    return !value;
  }

  public isNotEmpty(value: any, filter: Filter): boolean {
    return !!value;
  }

  public in(value: any, filter: Filter): boolean {
    return !!value && this.inList(value, filter);
  }

  private inList(value: any, filter: Filter): boolean {
    const filterValue = filter.value;
    if (!filterValue) {
      return true;
    }

    const stringValue = value.toString().toLowerCase();
    const filterValues = filterValue.toString().toLowerCase().split('||');

    for (let filterVal of filterValues) {
      if (stringValue === filterVal) {
        return true;
      }
    }

    return false;
  }

  public dateIsWithinNext(value: any, filter: Filter): boolean {
    const comparisonDate = this.processDateWithinNextCriteria(filter);
    const valueDate = moment.utc(value);
    // Set now slightly in the past to avoid issues with local time being slightly off
    const now = moment.utc().subtract(5, 'minutes');
    const comparisonValue = valueDate.isBefore(comparisonDate) && valueDate.isAfter(now);
    return comparisonValue;
  }

  public dateIsNotWithinNext(value: any, filter: Filter): boolean {
    const comparisonValue = this.dateIsWithinNext(value, filter);
    return !comparisonValue;
  }

  public dateIsGreaterThan(value: any, filter: Filter): boolean {
    // Set now slightly in the past to avoid issues with local time being slightly off
    const now = moment.utc().subtract(5, 'minutes');
    const comparisonDate = filter.value.toString().toLowerCase() === 'now' ? now : moment.utc(filter.value);
    const valueDate = moment.utc(value);
    const comparisonValue = valueDate.isAfter(comparisonDate);
    return comparisonValue;
  }

  public dateIsLessThan(value: any, filter: Filter): boolean {
    // Set now slightly in the future to avoid issues with local time being slightly off
    const now = moment.utc().add(5, 'minutes');
    const comparisonDate = filter.value.toString().toLowerCase() === 'now' ? now : moment.utc(filter.value);
    const valueDate = moment.utc(value);
    const comparisonValue = valueDate.isBefore(comparisonDate);
    return comparisonValue;
  }

  public containsNoCase(value: any, filter: Filter): boolean {
    return this.contains(value, filter);
  }

  public doesNotContainNoCase(value: any, filter: Filter): boolean {
    return this.doesNotContain(value, filter);
  }

  public notIn(value: any, filter: Filter): boolean {
    return !!value && !this.inList(value, filter);
  }

  public inASingleGeofence(value: any, filter: Filter, geofence: { [documentUId: string]: string }): boolean {
    if (!value) {
      return true; // null geometry is legal GeoJSON
    }
    const documentUId = filter.value?.split('||')[5];
    if (!geofence || !geofence[documentUId]) {
      return true;
    }
    const geofenceValues = JSON.parse(geofence[documentUId]);
    const checkValue = JSON.parse(value);

    try {
      if (!!geofenceValues) {
        return booleanIntersects(geofenceValues, checkValue);
      }
    } catch (e) {
      // Don't throw on failed comparison; may be non-compliant GeoJSON shape
      console.log(e);
    }
    return true;
  }

  public notInASingleGeofence(value: any, filter: Filter, geofence: { [documentUId: string]: string }): boolean {
    if (!value) {
      return true; // null geometry is legal GeoJSON
    }
    const documentUId = filter.value?.split('||')[5];
    if (!geofence || !geofence[documentUId]) {
      return true;
    }
    const geofenceValues = JSON.parse(geofence[documentUId]);
    const checkValue = JSON.parse(value);

    try {
      if (!!geofenceValues) {
        return booleanDisjoint(geofenceValues, checkValue);
      }
    } catch (e) {
      // Don't throw on failed comparison; may be non-compliant GeoJSON shape
      console.log(e);
    }
    return true;
  }

  processDateWithinLastCriteria(filter: Filter) {
    let dateTimeValue = moment.utc();

    let interval = parseInt(filter.value);
    interval = !isNaN(interval) ? interval : 0;
    var filterOptions = DateFilterOptions.Days;
    if (!!filter.options) {
      filterOptions = DateFilterOptions[DateFilterOptions[filter.options]];
    }

    if (!!filter.filterOptions) {
      const altFilterOptionValue = parseInt(filter.filterOptions[filterOptionKeys.dateFilterOptions]);
      if (!isNaN(altFilterOptionValue)) {
        filterOptions = DateFilterOptions[DateFilterOptions[altFilterOptionValue]];
      }
    }

    if (filterOptions == DateFilterOptions.Hours) {
      dateTimeValue = dateTimeValue.subtract(interval, 'hours');
    } else if (filterOptions == DateFilterOptions.Days) {
      dateTimeValue = dateTimeValue.subtract(interval, 'days');
    } else if (filterOptions == DateFilterOptions.Weeks) {
      dateTimeValue = dateTimeValue.subtract(interval, 'weeks');
    } else if (filterOptions == DateFilterOptions.Months) {
      dateTimeValue = dateTimeValue.subtract(interval, 'months');
    } else if (filterOptions == DateFilterOptions.Years) {
      dateTimeValue = dateTimeValue.subtract(interval, 'years');
    }

    return dateTimeValue;
  }

  processDateWithinNextCriteria(filter: Filter) {
    let dateTimeValue = moment.utc();

    let interval = parseInt(filter.value);
    interval = !isNaN(interval) ? interval : 0;

    var filterOptions = DateFilterOptions.Days;
    if (!!filter.options) {
      filterOptions = DateFilterOptions[DateFilterOptions[filter.options]];
    }

    if (!!filter.filterOptions) {
      const altFilterOptionValue = parseInt(filter.filterOptions[filterOptionKeys.dateFilterOptions]);
      if (!isNaN(altFilterOptionValue)) {
        filterOptions = DateFilterOptions[DateFilterOptions[altFilterOptionValue]];
      }
    }

    if (filterOptions == DateFilterOptions.Hours) {
      dateTimeValue = dateTimeValue.add(interval, 'hours');
    } else if (filterOptions == DateFilterOptions.Days) {
      dateTimeValue = dateTimeValue.add(interval, 'days');
    } else if (filterOptions == DateFilterOptions.Weeks) {
      dateTimeValue = dateTimeValue.add(interval, 'weeks');
    } else if (filterOptions == DateFilterOptions.Months) {
      dateTimeValue = dateTimeValue.add(interval, 'months');
    } else if (filterOptions == DateFilterOptions.Years) {
      dateTimeValue = dateTimeValue.add(interval, 'years');
    }

    return dateTimeValue;
  }
}
